import React from 'react';
import { Field } from 'react-final-form';
import AnimateHeight from 'react-animate-height';

export enum FormTextBoxDataType {
    String,
    Int,
    Float,
    Date,
    DateTime,
    PhoneNumber,
}

interface IFormTextboxProps {
    name: string;
    label?: string;
    isDisabled?: boolean;
    isPassword?: boolean;
    isRequired?: boolean;
    className?: string;
    error?: string;
    tabIndex?: number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    format?: (name: string) => string;
    formatOnBlur?: boolean;
    autocomplete?: string;
    style?: React.CSSProperties;
    type?: FormTextBoxDataType;
    submitOnEnter?: boolean;
    isHighlighted?: boolean;
}

const FormTextBox = (props: IFormTextboxProps) => {
    const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (!props.submitOnEnter) {
                event.preventDefault();
            }

            if (props.onEnter) {
                props.onEnter(event);
            }
        }
    };

    // Parse function declarations
    const parseNumFn: (value: string, name: string) => number = undefined;
    const parseStringFn: (value: string, name: string) => string = undefined;

    // Field component props for ints & floats, phone numbers, and common
    let intAndfloatProps = {
        parse: parseNumFn,
        type: 'string',
    };

    let phoneNumberProps = {
        parse: parseStringFn,
        type: 'string',
    };

    const commonProps = {
        name: props.name,
        onFocus: props.onFocus,
        formatOnBlur: props.formatOnBlur,
    };

    if (props.type && props.type === FormTextBoxDataType.Int) {
        intAndfloatProps = {
            parse: (value: string, name: string) => (isNaN(parseInt(value, 10)) ? undefined : parseInt(value, 10)),
            type: 'number',
        };
    }
    if (props.type && props.type === FormTextBoxDataType.Float) {
        intAndfloatProps = {
            parse: (value: string, name: string) => (isNaN(parseFloat(value)) ? undefined : parseFloat(value)),
            type: 'number',
        };
    }

    if (props.type && props.type === FormTextBoxDataType.PhoneNumber) {
        // Formats a phone number in the format (XXX)-XXX-XXXX
        const normalizePhoneNumber = (phoneNumber: string, name: string) => {
            if (!phoneNumber) {
                return phoneNumber;
            }
            const onlyNums = phoneNumber.replace(/[^\d]/g, '');
            if (onlyNums.length <= 3) {
                return onlyNums;
            }
            if (onlyNums.length <= 7) {
                return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 7)}`;
            }
            return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
        };
        phoneNumberProps = {
            parse: normalizePhoneNumber,
            type: 'string',
        };
    }

    if (props.type && (props.type === FormTextBoxDataType.Int || props.type === FormTextBoxDataType.Float)) {
        return (
            <Field {...commonProps} {...intAndfloatProps}>
                {({ input, meta }) => {
                    const hasError = meta.submitFailed && meta.error;
                    let labelClass = input.value ? 'reduced ' : ' ';
                    labelClass += props.isRequired ? 'required ' : '';
                    labelClass += hasError ? 'error' : '';
                    labelClass += props.isDisabled ? 'disabled ' : '';
                    let inputClass = hasError ? 'error ' : '';
                    inputClass += props.isDisabled ? 'disabled ' : '';
                    const errorHeight = hasError ? 'auto' : 0;

                    return (
                        <div className={`dlc-form-textbox-wrapper ${props.className || ''}`} style={props.style}>
                            <div className={`dlc-form-textbox ${props.isDisabled ? 'disabled' : ''}`}>
                                <input
                                    id={props.name}
                                    className={props.isHighlighted ? `dlc-form-textbox-input-highlight ${inputClass}` : `dlc-form-textbox-input ${inputClass}`}
                                    style={props?.style}
                                    tabIndex={props.tabIndex}
                                    {...input}
                                    type={'number'}
                                    onKeyPress={onKeyPress}
                                    onChange={(e) => {
                                        input.onChange(e);
                                        if (props.onChange) {
                                            props.onChange(e);
                                        }
                                    }}
                                    onFocus={(e) => {
                                        input.onFocus(e);
                                        if (props.onFocus) {
                                            props.onFocus(e);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        input.onBlur(e);
                                        if (props.onBlur) {
                                            props.onBlur(e);
                                        }
                                    }}
                                    autoComplete={props.autocomplete || 'new-field-1'}
                                    disabled={props.isDisabled || false}
                                />
                                <div className={`dlc-form-textbox-label ${labelClass}`}>
                                    {props.label}
                                </div>
                            </div>
                            <AnimateHeight height={errorHeight} duration={300}>
                                <div className="dlc-form-textbox-error">{meta.error}</div>
                            </AnimateHeight>
                        </div>
                    );
                }}
            </Field>
        );
    } else {
        return (
            <Field
                {...commonProps}
                {...(props.type && props.type === FormTextBoxDataType.PhoneNumber ? { ...phoneNumberProps } : undefined)} // Don't add any additional props }
            >
                {({ input, meta }) => {
                    const hasError = meta.submitFailed && meta.error;
                    let labelClass = input.value ? 'reduced ' : ' ';
                    labelClass += props.isRequired ? 'required ' : '';
                    labelClass += hasError ? 'error' : '';
                    labelClass += props.isDisabled ? 'disabled ' : '';
                    let inputClass = hasError ? 'error ' : '';
                    inputClass += props.isDisabled ? 'disabled ' : '';
                    const errorHeight = hasError ? 'auto' : 0;

                    let styles = props.style;

                    if (props.type === FormTextBoxDataType.Date) {
                        styles = { ...styles, height: '4rem' };
                    }

                    return (
                        <div className={`dlc-form-textbox-wrapper ${props.className || ''}`} style={styles}>
                            <div className={`dlc-form-textbox ${props.isDisabled ? 'disabled' : ''}`}>
                                <input
                                    id={props.name}
                                    className={`dlc-form-textbox-input ${inputClass}`}
                                    tabIndex={props.tabIndex}
                                    {...input}
                                    style={props.type === FormTextBoxDataType.Date ? { height: '4.1rem' } : undefined}
                                    type={
                                        props.isPassword
                                            ? 'password'
                                            : props.type === FormTextBoxDataType.Date
                                            ? 'date'
                                            : props.type === FormTextBoxDataType.DateTime
                                            ? 'datetime-local'
                                            : 'text'
                                    }
                                    onKeyPress={onKeyPress}
                                    onChange={(e) => {
                                        input.onChange(e);
                                        if (props.onChange) {
                                            props.onChange(e);
                                        }
                                    }}
                                    onFocus={(e) => {
                                        input.onFocus(e);
                                        if (props.onFocus) {
                                            props.onFocus(e);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        input.onBlur(e);
                                        if (props.onBlur) {
                                            props.onBlur(e);
                                        }
                                    }}
                                    autoComplete={props.autocomplete || 'new-field-1'}
                                    disabled={props.isDisabled || false}
                                />
                                <div className={`dlc-form-textbox-label ${labelClass}`}>{props.label}</div>
                            </div>
                            <AnimateHeight height={errorHeight} duration={300}>
                                <div className="dlc-form-textbox-error">{meta.error}</div>
                            </AnimateHeight>
                        </div>
                    );
                }}
            </Field>
        );
    }
};

export default FormTextBox;



// WEBPACK FOOTER //
// ./src/components/controls/forms/redesign/FormTextbox.tsx